/**
 * Tariff Transformation Utilities
 *
 * Consolidated module for all tariff format conversions.
 * Supports the simplified tariff architecture (Vortx Flux Rate Database only):
 * - Blended rates (simple $/kWh + $/kW)
 * - TOU rates (8760 hourly energy rates + demand charges)
 *
 * All tariffs come from our admin-approved database.
 */

// Re-export validation and normalization from schema
export {
  ElectricTariffSchema,
  BlendedTariffSchema,
  TOUTariffSchema,
  GasTariffSchema,
  touGridTo8760,
  rates8760ToTouGrid,
  normalizeTariff,
  validateTariff,
} from '../schemas/tariff';

// Constants
const HOURLY_LENGTH = 8760;
const MONTHS_LENGTH = 12;
const HOURS_PER_DAY = 24;
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Convert TOU windows array to 12x24 schedule grid
 *
 * Windows format: Array of { periodId, startHour, endHour, months, weekdays, weekends }
 * Grid format: { weekday: number[][], weekend: number[][] } - 12 months x 24 hours
 *
 * @param {Array} windowsArray - Array of window objects
 * @param {Array} periodsArray - Array of period objects (for base period ID)
 * @returns {Object} { weekday: number[][], weekend: number[][] }
 */
export const convertWindowsToGrid = (windowsArray, periodsArray) => {
  // Determine base period (default to first period or id 0)
  const baseId = periodsArray?.[0]?.id ?? 0;

  // Create empty 12x24 grids filled with base period
  const weekdayGrid = Array(MONTHS_LENGTH).fill(null).map(() => Array(HOURS_PER_DAY).fill(baseId));
  const weekendGrid = Array(MONTHS_LENGTH).fill(null).map(() => Array(HOURS_PER_DAY).fill(baseId));

  if (!windowsArray || !Array.isArray(windowsArray)) {
    return { weekday: weekdayGrid, weekend: weekendGrid };
  }

  // Apply each window to the grid
  windowsArray.forEach(win => {
    const { periodId, startHour, endHour, months, weekdays, weekends } = win;

    if (!months || !Array.isArray(months)) return;

    months.forEach(monthIdx => {
      if (monthIdx < 0 || monthIdx > 11) return;

      // Standard hour range (start < end)
      if (startHour <= endHour) {
        for (let hour = startHour; hour < endHour && hour < HOURS_PER_DAY; hour++) {
          if (weekdays) weekdayGrid[monthIdx][hour] = periodId;
          if (weekends) weekendGrid[monthIdx][hour] = periodId;
        }
      }
      // Overnight range would be handled here if needed
    });
  });

  return { weekday: weekdayGrid, weekend: weekendGrid };
};

/**
 * Convert 12x24 schedule grid back to windows array
 *
 * Optimizes by grouping months with identical patterns and
 * combining contiguous hour blocks.
 *
 * @param {Object} schedule - { weekday: number[][], weekend: number[][] }
 * @param {Array} periodsArray - Array of periods
 * @returns {Array} Array of optimized window objects
 */
export const convertGridToWindows = (schedule, periodsArray) => {
  const newWindows = [];
  let windowId = 0;
  const basePeriodId = periodsArray?.[0]?.id ?? 0;

  if (!schedule) return [];

  // Process both weekday and weekend grids
  ['weekday', 'weekend'].forEach(dayType => {
    const grid = schedule[dayType];
    if (!grid) return;

    // Group months with identical patterns to minimize window count
    const patternGroups = {};
    grid.forEach((hourRow, monthIdx) => {
      const key = JSON.stringify(hourRow);
      if (!patternGroups[key]) {
        patternGroups[key] = { months: [], hours: hourRow };
      }
      patternGroups[key].months.push(monthIdx);
    });

    // For each pattern group, extract contiguous hour blocks
    Object.values(patternGroups).forEach(({ months, hours }) => {
      let currentPeriod = hours[0];
      let startHour = 0;

      for (let hour = 1; hour <= HOURS_PER_DAY; hour++) {
        const periodId = hour < HOURS_PER_DAY ? hours[hour] : -1; // Force closure at end

        if (periodId !== currentPeriod || hour === HOURS_PER_DAY) {
          // Skip base period windows (they're implicit background)
          if (currentPeriod !== basePeriodId && currentPeriod !== undefined) {
            newWindows.push({
              id: windowId++,
              periodId: currentPeriod,
              startHour,
              endHour: hour,
              months: [...months],
              weekdays: dayType === 'weekday',
              weekends: dayType === 'weekend',
            });
          }
          startHour = hour;
          currentPeriod = periodId;
        }
      }
    });
  });

  return newWindows;
};

/**
 * Convert TOU editor data to solver format
 *
 * Takes the UI-friendly TOU format (periods, windows, schedule grids)
 * and converts to the format expected by the solver.
 *
 * @param {Object} touData - TOU format { seasons, periods, windows, schedule, fixedMonthlyFee, facilityDemand }
 * @returns {Object} Solver-compatible format with energyratestructure, schedules, etc.
 */
export const touDataToSolverFormat = (touData) => {
  if (!touData || !touData.periods) {
    return null;
  }

  const { periods, schedule, fixedMonthlyFee, facilityDemand } = touData;

  // Build energy rate structure: [[{rate, adj, sell, unit}], ...]
  const energyratestructure = periods.map(period => [{
    rate: parseFloat(period.energy) || 0,
    adj: 0,
    sell: 0,
    unit: 'kWh',
  }]);

  // Build demand rate structure
  const demandratestructure = periods.map(period => [{
    rate: parseFloat(period.demand) || 0,
    adj: 0,
    unit: 'kW',
  }]);

  // Build period ID to index map
  const periodIdToIndex = {};
  periods.forEach((p, idx) => {
    periodIdToIndex[p.id] = idx;
  });

  // Convert schedule grids to solver format (period indices, not IDs)
  const convertSchedule = (scheduleData) => {
    if (!scheduleData || scheduleData.length === 0) {
      return Array(MONTHS_LENGTH).fill(null).map(() => Array(HOURS_PER_DAY).fill(0));
    }

    // Handle flat (288 elements) vs matrix (12 rows) formats
    if (typeof scheduleData[0] === 'number') {
      if (scheduleData.length === 288) {
        const result = [];
        for (let month = 0; month < MONTHS_LENGTH; month++) {
          const row = scheduleData.slice(month * HOURS_PER_DAY, (month + 1) * HOURS_PER_DAY);
          result.push(row.map(pid => periodIdToIndex[pid] ?? 0));
        }
        return result;
      }
      return Array(MONTHS_LENGTH).fill(null).map(() => Array(HOURS_PER_DAY).fill(0));
    }

    // Matrix format
    return scheduleData.map(monthRow =>
      monthRow.map(periodId => periodIdToIndex[periodId] ?? 0)
    );
  };

  const energyweekdayschedule = convertSchedule(schedule?.weekday);
  const energyweekendschedule = convertSchedule(schedule?.weekend);

  // Flat demand structure for facility demand
  const flatdemandstructure = facilityDemand ? [[{
    rate: parseFloat(facilityDemand) || 0,
    adj: 0,
    unit: 'kW',
  }]] : [];

  return {
    energyratestructure,
    demandratestructure,
    flatdemandstructure,
    energyweekdayschedule,
    energyweekendschedule,
    demandweekdayschedule: energyweekdayschedule,
    demandweekendschedule: energyweekendschedule,
    fixedmonthlycharge: parseFloat(fixedMonthlyFee) || 0,
    minmonthlycharge: 0,
    flatdemandmonths: Array(MONTHS_LENGTH).fill(0),
  };
};


/**
 * Convert TOU periods/schedule to 8760 hourly energy rates
 *
 * Takes the UI TOU data and generates the full year of hourly rates
 * that gets sent to the solver.
 *
 * @param {Object} touData - { periods, schedule }
 * @returns {number[]} 8760 hourly energy rates
 */
export const touDataTo8760 = (touData) => {
  if (!touData?.periods || !touData?.schedule) {
    throw new Error('TOU data must have periods and schedule');
  }

  const { periods, schedule } = touData;
  const rates = [];

  // Build period ID to rate map
  const periodRates = {};
  periods.forEach(p => {
    periodRates[p.id] = parseFloat(p.energy) || 0;
  });

  // Generate 8760 rates from schedule
  for (let month = 0; month < MONTHS_LENGTH; month++) {
    const daysInMonth = DAYS_IN_MONTH[month];
    const weekdaySchedule = schedule.weekday?.[month] || [];
    const weekendSchedule = schedule.weekend?.[month] || [];

    for (let day = 0; day < daysInMonth; day++) {
      // Determine day of week (simplified - assumes Jan 1 is Monday)
      const dayOfYear = DAYS_IN_MONTH.slice(0, month).reduce((a, b) => a + b, 0) + day;
      const isWeekend = (dayOfYear % 7) >= 5; // Sat=5, Sun=6
      const hourlySchedule = isWeekend ? weekendSchedule : weekdaySchedule;

      for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
        const periodId = hourlySchedule[hour] ?? 0;
        const rate = periodRates[periodId] ?? 0;
        rates.push(rate);
      }
    }
  }

  return rates;
};

/**
 * Create a blended tariff from basic inputs
 *
 * @param {Object} params - { energyRate, demandRate, fixedCharge, exportRate }
 * @returns {Object} Blended tariff object
 */
export const createBlendedTariff = ({
  energyRate,
  demandRate = 0,
  fixedCharge = 0,
  exportRate = 0,
  name = 'Custom Blended Rate',
  utilityName = '',
}) => ({
  type: 'blended',
  blended_annual_energy_rate: energyRate,
  blended_annual_demand_rate: demandRate,
  fixed_monthly_charge: fixedCharge,
  min_monthly_charge: 0,
  export_rate: exportRate,
  tariff_name: name,
  utility_name: utilityName,
});

/**
 * Create a TOU tariff from 8760 rates
 *
 * @param {Object} params - { energyRates, demandRates, fixedCharge, exportRate }
 * @returns {Object} TOU tariff object
 */
export const createTOUTariff = ({
  energyRates,
  monthlyDemandRates = null,
  fixedCharge = 0,
  exportRate = 0,
  name = 'Custom TOU Rate',
  utilityName = '',
}) => {
  if (!energyRates || energyRates.length !== HOURLY_LENGTH) {
    throw new Error(`TOU tariff requires ${HOURLY_LENGTH} hourly energy rates`);
  }

  return {
    type: 'tou',
    energy_rates: energyRates,
    monthly_demand_rates: monthlyDemandRates,
    fixed_monthly_charge: fixedCharge,
    min_monthly_charge: 0,
    export_rate: exportRate,
    tariff_name: name,
    utility_name: utilityName,
  };
};

/**
 * Calculate average energy rate from any tariff
 *
 * @param {Object} tariff - Blended or TOU tariff
 * @returns {number} Average $/kWh rate
 */
export const getAverageEnergyRate = (tariff) => {
  if (!tariff) return 0;

  if (tariff.type === 'blended') {
    return tariff.blended_annual_energy_rate || 0;
  }

  if (tariff.type === 'tou' && tariff.energy_rates) {
    const sum = tariff.energy_rates.reduce((a, b) => a + b, 0);
    return sum / tariff.energy_rates.length;
  }

  // Legacy format fallback
  if (tariff.blended_annual_energy_rate) {
    return tariff.blended_annual_energy_rate;
  }

  return 0;
};

/**
 * Calculate annual cost estimate for a load profile
 *
 * @param {Object} tariff - Electric tariff
 * @param {number[]} loadKw - 8760 or 35040 load profile
 * @returns {Object} { energy_cost, demand_cost, fixed_cost, total }
 */
export const estimateAnnualCost = (tariff, loadKw) => {
  if (!tariff || !loadKw || loadKw.length === 0) {
    return { energy_cost: 0, demand_cost: 0, fixed_cost: 0, total: 0 };
  }

  // Determine time step (1 hour or 15 min)
  const isSubHourly = loadKw.length > HOURLY_LENGTH;
  const timeStep = isSubHourly ? 0.25 : 1;

  // Calculate energy consumption
  const totalKwh = loadKw.reduce((a, b) => a + b, 0) * timeStep;

  let energyCost = 0;
  if (tariff.type === 'blended') {
    energyCost = totalKwh * (tariff.blended_annual_energy_rate || 0);
  } else if (tariff.type === 'tou' && tariff.energy_rates) {
    // Apply hourly rates
    for (let i = 0; i < loadKw.length; i++) {
      const hourIndex = isSubHourly ? Math.floor(i / 4) : i;
      const rate = tariff.energy_rates[hourIndex % HOURLY_LENGTH] || 0;
      energyCost += loadKw[i] * timeStep * rate;
    }
  }

  // Calculate demand cost (monthly peak * demand rate)
  let demandCost = 0;
  const demandRate = tariff.type === 'blended'
    ? tariff.blended_annual_demand_rate || 0
    : tariff.monthly_demand_rates
      ? tariff.monthly_demand_rates.reduce((a, b) => a + b, 0) / MONTHS_LENGTH
      : 0;

  if (demandRate > 0) {
    // Find monthly peaks
    const monthlyPeaks = Array(MONTHS_LENGTH).fill(0);
    let hourIndex = 0;
    for (let month = 0; month < MONTHS_LENGTH; month++) {
      const hoursInMonth = DAYS_IN_MONTH[month] * HOURS_PER_DAY;
      const intervalsInMonth = isSubHourly ? hoursInMonth * 4 : hoursInMonth;
      for (let i = 0; i < intervalsInMonth && hourIndex < loadKw.length; i++, hourIndex++) {
        monthlyPeaks[month] = Math.max(monthlyPeaks[month], loadKw[hourIndex]);
      }
    }
    demandCost = monthlyPeaks.reduce((a, b) => a + b, 0) * demandRate;
  }

  // Fixed costs
  const fixedCost = (tariff.fixed_monthly_charge || 0) * MONTHS_LENGTH;

  return {
    energy_cost: energyCost,
    demand_cost: demandCost,
    fixed_cost: fixedCost,
    total: energyCost + demandCost + fixedCost,
  };
};

export default {
  convertWindowsToGrid,
  convertGridToWindows,
  touDataToSolverFormat,
  touDataTo8760,
  createBlendedTariff,
  createTOUTariff,
  getAverageEnergyRate,
  estimateAnnualCost,
};
